<template>
  <v-dialog
    v-model="internalValue"
    class="egov-modal-overlay d-flex align-center justify-center"
    :persistent="persistent"
    scrollable
    :aria-labelledby="titleId"
    :aria-describedby="titleId"
    @keydown.esc="handleClose"
    @update:modelValue="(v) => !v && handleClose()"
  >
    <v-card
      class="egov-modal"
      :class="`modal-size-${size}`"
      elevation="0"
      :style="{ backgroundColor: modalBg, boxShadow: 'none' }"
      ref="dialogRef"
    >
      <EgovIconButton
        v-if="showCloseIcon"
        icon="ri-close-line"
        ariaLabel="Close"
        variant="text"
        class="close-btn"
        size="small"
        :style="{ color: iconColor }"
        @click="handleClose"
      />

      <div v-if="icon" class="icon-row" aria-hidden="true">
        <v-icon size="30" class="modal-svg-icon" :style="{ color: iconColor }" :icon="icon" />
      </div>

      <header class="egov-modal__header" :class="icon ? 'center' : 'start'">
        <h1 class="modal-title" :id="titleId" :style="{ color: textColor }" :class="{ 'with-icon': !!icon }">
          {{ title || 'Dialog' }}
        </h1>
      </header>

      <section class="egov-modal__body">
        <slot name="content" />
      </section>

      <footer v-if="hasCtas" class="egov-modal__footer">
        <slot name="footer">
          <EgovButton
            v-if="secondaryCta"
            :label="secondaryCta.label"
            :ariaLabel="`${secondaryCta.label} - ${title || 'Dialog'}`"
            :variant="secondaryCta.variant || 'text'"
            :appendIcon="secondaryCta.appendIcon"
            @click="() => (typeof secondaryCta.onClick === 'function' ? secondaryCta.onClick() : handleClose())"
          />
          <EgovButton
            v-if="primaryCta"
            ref="primaryCtaRef"
            :label="primaryCta.label"
            :ariaLabel="`${primaryCta.label} - ${title || 'Dialog'}`"
            :variant="primaryCta.variant || 'filled'"
            :backgroundColor="primaryCta.backgroundColor || ''"
            :appendIcon="primaryCta.appendIcon"
            @click="() => (typeof primaryCta.onClick === 'function' ? primaryCta.onClick() : handleClose())"
          />
        </slot>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import EgovIconButton from './EgovIconButton.vue'
import EgovButton from './EgovButton.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: 'Modal Title' },
  icon: String,
  showCloseIcon: { type: Boolean, default: true },
  primaryCta: Object,
  secondaryCta: Object,
  persistent: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large', 'x-large'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue', 'close'])

const internalValue = ref(props.modelValue)
watch(
  () => props.modelValue,
  (v) => (internalValue.value = v),
)
watch(internalValue, (v) => emit('update:modelValue', v))

const titleId = `modal-title-${uuidv4()}`
const dialogRef = ref(null)
const primaryCtaRef = ref(null)

watch(internalValue, async (open) => {
  if (!open) return
  await nextTick()
  const primaryBtn = primaryCtaRef.value?.$el?.querySelector('button') ?? primaryCtaRef.value?.$el
  if (primaryBtn) {
    primaryBtn.focus()
    return
  }
  const dialog = dialogRef.value?.$el || dialogRef.value
  dialog?.querySelector('button:not([disabled])')?.focus()
})

const handleClose = () => {
  internalValue.value = false
  emit('close')
}

const hasCtas = computed(() => props.primaryCta || props.secondaryCta)

const modalBg = 'rgba(223, 232, 255, 1)'
const textColor = 'var(--kds-on-surface)'
const iconColor = 'rgba(82, 96, 112, 1)'
</script>

<style scoped lang="scss">
.egov-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  border-radius: 12px !important;
  padding: 4px;
  width: 100%;
  margin: 0 auto;

  // Size variants for desktop and tablet
  @media (min-width: 960px) {
    &.modal-size-small {
      max-width: 400px;
    }
    &.modal-size-medium {
      max-width: 600px;
    }
    &.modal-size-large {
      max-width: 800px;
    }
    &.modal-size-x-large {
      max-width: 1000px;
    }
  }

  @media (min-width: 600px) and (max-width: 959px) {
    &.modal-size-small {
      max-width: 450px;
    }
    &.modal-size-medium {
      max-width: 550px;
    }
    &.modal-size-large {
      max-width: 700px;
    }
    &.modal-size-x-large {
      max-width: 85%;
    }
  }

  // Mobile: all sizes converge to same margin-based width
  @media (max-width: 599px) {
    max-height: 95vh;
    max-width: calc(100% - 16px);
    border-radius: 8px;
    // No max-width constraints on mobile - uses margin only
  }
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 1;

  @media (max-width: 599px) {
    top: 12px;
    right: 12px;
  }
}

.icon-row {
  display: flex;
  justify-content: center;
  padding-top: 20px;

  @media (max-width: 599px) {
    padding-top: 16px;
  }
}

.egov-modal__header {
  display: flex;
  padding: 20px 40px 12px;

  @media (max-width: 599px) {
    padding: 16px 20px 8px;
  }

  &.center {
    justify-content: center;
  }
  &.start {
    justify-content: flex-start;
    margin-top: 5px;
    margin-left: -10px;

    @media (max-width: 599px) {
      margin-left: 0;
    }
  }
}

.modal-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-top: -5px;
  opacity: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
  color: var(--kds-on-surface);
  font-family: "Plus Jakarta Sans", Arial, Helvetica, sans-serif;

  @media (max-width: 599px) {
    font-size: 1.25rem;
  }
}

.egov-modal__body {
  padding: 0 30px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  color: var(--kds-on-surface-variant);

  @media (max-width: 599px) {
    padding: 0 20px 16px;
  }
}

.egov-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding: 16px 30px 24px;

  @media (max-width: 599px) {
    padding: 12px 20px 16px;
    gap: 10px;
    flex-wrap: wrap;
  }
}
</style>